import { useEffect, useState, use } from 'react';
import { useParams, useUrl } from 'crossroad';
import { usePlaceTransitionImage } from '@/lib/hooks/use-place-transition-image';
import { useCompositionPlaceImage } from '@/lib/hooks/use-composition-place-image';
import { useHeroPreloader } from '@/lib/hooks/use-hero-preloader';
import { useContextTransition } from '@/lib/hooks/use-context-transition';
import { useGeographicalTransition } from '@/lib/hooks/use-geographical-transition';
import PassageDetails from './passage-details';
import { PassageContextPanel } from './passage-context-panel';
import { PassageContextFooter } from './passage-context-footer';
import { GuidedStudySubheader } from './guided-study-subheader';
import { ContextTransitionOverlay } from './ContextTransitionOverlay';
import { GeographicalTransitionOverlay } from './GeographicalTransitionOverlay';
import { ManuscriptProvider } from '@/context/manuscript-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { OutlineDataContext } from '@/data/outline/outline-data-context';
import { usePlacesByPassageData } from '@/data/places/places-by-passage-data-context';
import { motion, AnimatePresence } from 'framer-motion';
import bgImage from '../../assets/glen-texture.jpg';
import { SEO } from '@/components/seo';
import type { ContextType } from './context-intro';

const contextTitles: Record<ContextType, string> = {
    archaeology: 'Archaeological Context',
    canon: 'Outline',
    culture: 'Cultural Context',
    historicalEvents: 'Timeline',
    places: 'Map',
    book: 'Book Context',
    scripture: 'Scriptural Context',
    literary: 'Literary Context',
    engagementMethods: 'Ideas for Engagement',
    inductive: 'Inductive Study',
    leaderToolkit: 'Leader\'s Toolkit',
};


interface PassageContextAnimatorProps {
    passageId: string;
}

/**
 * Orchestrates the context views without animations.
 * Provides the background, footer, and handles the context panel/subheader visibility.
 */
export function PassageContextAnimator({ passageId }: PassageContextAnimatorProps) {
    const contextCategory = useParams('/app/passage/:id/context/:contextCategory')?.contextCategory;
    const [showGuidedStudySubheader, setShowGuidedStudySubheader] = useState(false);
    const [, setUrl] = useUrl();

    // Get passage data (may be null during initial load)
    const passageContext = use(PassageDataContext);
    const passage = passageContext.data ? passageContext.get(passageId) : null;

    // Get outline data for calculating position in book
    const outlineContext = use(OutlineDataContext);

    // Preload transition images in background (happens on passage load)
    const placeTransitionImage = usePlaceTransitionImage(passageId);

    // Preload composition place image for literary and scriptural contexts
    useCompositionPlaceImage(passageId);

    // Preload static hero images for context panels
    useHeroPreloader();

    // Get primary place coordinates for geographical transition
    const { getPlacesByPassageByPassageId } = usePlacesByPassageData();
    const placesByPassage = getPlacesByPassageByPassageId(passageId);
    const primaryPlace = placesByPassage?.entities.find(e => e.importance === 'high') || placesByPassage?.entities[0];

    // Context transition animation (zoom to canon bar)
    const transitionMode = contextCategory === 'book' ? 'book' : 'bible';
    const transitionState = useContextTransition({
        passageBook: passage?.book || '',
        passageRef: passage?.ref || '',
        mode: transitionMode,
    });

    // Geographical transition animation (archaeologist's discovery)
    const geographicalTransitionState = useGeographicalTransition({
        passageId,
        reconstructionImageUrl: placeTransitionImage.reconstructionImageUrl,
        placeName: placeTransitionImage.placeName,
        placeCoordinates: primaryPlace?.coordinates,
    });


    // Close subheader when navigating to a different passage
    useEffect(() => {
        setShowGuidedStudySubheader(false);
    }, [passageId]);

    // SEO Logic
    const pageTitle = passage
        ? contextCategory
            ? `${contextTitles[contextCategory as ContextType] || 'Context'} - ${passage.book} ${passage.ref}`
            : `${passage.book} ${passage.ref} - Bible Context & History`
        : 'BibleGlen';

    const pageDescription = passage
        ? contextCategory
            ? `Explore the ${contextTitles[contextCategory as ContextType] || 'context'} for ${passage.book} ${passage.ref}. ${passage.summary ? passage.summary.substring(0, 150) + '...' : ''}`
            : passage.summary
        : 'Explore the Bible with rich historical, cultural, and literary context.';

    return (
        <ManuscriptProvider passageId={passageId}>
            <SEO
                title={pageTitle}
                description={pageDescription}
            />

            {/* Fixed full-screen background (moved from PassageDetails) */}
            <div
                className="fixed inset-0 z-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${bgImage})` }}
            >
                <div className="absolute inset-0 bg-background/80 backdrop-blur-2xl" />
            </div>

            {/* Guided Study Subheader (Refined with instant visibility) */}
            <AnimatePresence>
                {showGuidedStudySubheader && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                        className="z-40 border-b border-primary/20 bg-background/50 backdrop-blur-md"
                    >
                        <GuidedStudySubheader
                            passageId={passageId}
                            bookName={passage?.book}
                            onClose={() => {
                                setShowGuidedStudySubheader(false);
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Original passage */}
            <div
                style={{
                    pointerEvents: contextCategory === 'guidedStudy' ? 'none' : 'auto',
                }}
            >
                <PassageDetails passageId={passageId} />
            </div>


            {/* Footer - always visible */}
            {
                passageId && (
                    <div className="fixed bottom-0 w-full bg-background/75 backdrop-blur-xl border-t z-[120] shadow-[0_-10px_15px_-3px_rgba(0,0,0,0.1),_0_-4px_6px_-4px_rgba(0,0,0,0.1)]">
                        <PassageContextFooter
                            passageId={passageId}
                            onBeginGuidedStudy={() => {
                                // If we're currently in a context page, navigate back to the base passage view
                                // This closes the context panel before showing the guided study subheader
                                if (contextCategory) {
                                    setUrl(`/app/passage/${passageId}`);
                                }

                                // Reverting to simple slide-down as requested
                                setShowGuidedStudySubheader(true);
                            }}
                        />
                    </div>
                )
            }

            {/* Context panel (pops in instantly) - Skip for Guided Study which uses subheader */}
            {
                passageId && contextCategory !== 'guidedStudy' && (
                    <AnimatePresence mode="popLayout">
                        {contextCategory && (
                            <PassageContextPanel
                                key={contextCategory}
                                passageId={passageId}
                                isTransitionAnimating={transitionState.isAnimating}
                                isGeoAnimating={geographicalTransitionState.isAnimating}
                                geoStage={geographicalTransitionState.stage}
                                geoMapCenter={geographicalTransitionState.mapCenter}
                                setGeoMarkerPosition={geographicalTransitionState.setMarkerPosition}
                            />
                        )}
                    </AnimatePresence>
                )
            }

            {/* Context transition overlay (zoom to canon bar animation) */}
            <ContextTransitionOverlay
                isAnimating={transitionState.isAnimating}
                sourceRect={transitionState.sourceRect}
                targetRect={transitionState.targetRect}
                targetPercent={transitionState.targetPercent}
                onAnimationComplete={transitionState.endAnimation}
            />

            {/* Geographical transition overlay (camera pullback animation) */}
            <GeographicalTransitionOverlay
                isAnimating={geographicalTransitionState.isAnimating}
                stage={geographicalTransitionState.stage}
                sourceRect={geographicalTransitionState.sourceRect}
                markerPosition={geographicalTransitionState.markerPosition}
                reconstructionImageUrl={geographicalTransitionState.reconstructionImageUrl}
                onAnimationComplete={geographicalTransitionState.endAnimation}
            />

        </ManuscriptProvider >
    );
}
